import React from "react";
import { CheckCircle2, XCircle, MoveHorizontal } from "lucide-react";

/**
 * Drag-to-compare slider for before/after screenshots.
 * Pointer + keyboard accessible.
 */
export default function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeLabel = "Vorher",
  afterLabel = "Nachher",
  beforeNote = "Datenqualität 31/100",
  afterNote = "Datenqualität 96/100",
}) {
  const [pos, setPos] = React.useState(50);
  const wrap = React.useRef(null);
  const dragging = React.useRef(false);

  const setFromClientX = React.useCallback((clientX) => {
    const el = wrap.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 2));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 2));
  };

  return (
    <div
      ref={wrap}
      data-testid="ba-slider"
      className="relative w-full overflow-hidden rounded-xl border border-slate-200 bg-white ring-soft-lg select-none touch-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* After (base layer) */}
      <img
        src={afterSrc}
        alt={`${afterLabel} — ${afterNote}`}
        className="block w-full h-auto"
        loading="lazy"
        decoding="async"
      />

      {/* Before (clipped overlay) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
        aria-hidden="true"
      >
        <img
          src={beforeSrc}
          alt=""
          className="block h-full w-auto max-w-none"
          style={{
            width: `${(100 / Math.max(pos, 0.001)) * 100}%`,
          }}
          loading="lazy"
          decoding="async"
        />
        {/* Subtle desaturation tint for "before" */}
        <div className="absolute inset-0 bg-rose-500/[0.04]" />
      </div>

      {/* Labels */}
      <div className="absolute left-3 top-3 inline-flex items-center gap-2 bg-white/95 backdrop-blur border border-rose-200 rounded-md px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-rose-700">
        <XCircle className="size-3.5" />
        {beforeLabel} · {beforeNote}
      </div>
      <div className="absolute right-3 top-3 inline-flex items-center gap-2 bg-white/95 backdrop-blur border border-cyan-200 rounded-md px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-cyan-700">
        <CheckCircle2 className="size-3.5" />
        {afterLabel} · {afterNote}
      </div>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-cyan-500/80 pointer-events-none shadow-[0_0_0_1px_rgba(8,145,178,0.15)]"
        style={{ left: `${pos}%` }}
      />
      <button
        type="button"
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        aria-label="Vorher/Nachher-Vergleich"
        onKeyDown={onKey}
        data-testid="ba-handle"
        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 size-11 rounded-full bg-white border-2 border-cyan-500 ring-soft-lg cursor-ew-resize flex items-center justify-center text-cyan-700 hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2"
        style={{ left: `${pos}%` }}
      >
        <MoveHorizontal className="size-4" />
      </button>

      {/* Bottom hint */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 bg-white/90 backdrop-blur border border-slate-200 rounded-md px-2 py-1 pointer-events-none">
        Ziehen zum Vergleichen
      </div>
    </div>
  );
}
